<template>
  <div v-if="state === 'transcribing'" class="mdr-listening-prompt">
    <span class="mdr-row">
      <button class="waveform-ico">
        <img src="@/assets/img/voice-waveform.svg" />
      </button>
      <button
        @click="handleTranslateClick"
        :disabled="!transcription"
        class="mdr-btn-stop"
      />
    </span>
    <span class="info">
      {{ $t("call.recordTranslator.transcribing") }}
    </span>
  </div>
  <ul v-if="transcription" class="mdr-upps">
    <li>{{ transcription }}</li>
  </ul>

  <ul v-if="expertTranslation?.text" class="mdr-subs">
    <li>
      <template
        v-for="(t, i) in map(split(expertTranslation.text, '\n'), trim)"
        :key="i"
      >
        {{ t }}<br v-if="i < split(expertTranslation.text, '\n').length - 1" />
      </template>
    </li>
    <ProgressBar
      :key="key"
      :duration="getDuration(expertTranslation.text)"
      @end="unsetExpertTranslation"
      :is-paused="isExpertTranslationPaused"
    />
  </ul>
</template>

<script lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  MessageType,
  HumanSpeechLanguage,
  RequestedAction,
  EndpointStatus
} from "@moderrotech/servers-communication-library/src/types";
import _ from "lodash";
import { showToastError } from "@/helpers/notification";
import { i18n } from "@/lang";
import ProgressBar from "@/components/ProgressBar.vue";

export default {
  components: { ProgressBar },
  setup() {
    const store = useStore();
    const state = ref<"pending" | "transcribing" | "translating">("pending");

    watch(
      () => state.value,
      v => {
        if (store.getters.endpointStatusCode === EndpointStatus.connected) {
          store.dispatch("session/sendJsonMessage", {
            message: {
              type: MessageType.recordTranslationState,
              state: v
            }
          });
        }
      },
      { immediate: true }
    );

    const getProperty = (name: string) => store.getters["auth/property"](name),
      sourceLanguage = computed(
        (): HumanSpeechLanguage | null =>
          store.state.session.sourceLanguage ??
          getProperty("aws.translation.source.language") ??
          null
      ),
      targetLanguage = computed(
        (): HumanSpeechLanguage | null => store.state.session.targetLanguage
      );

    const transcription = computed(
      (): string | null => store.state.session.recordTranscription
    );
    watch(
      () => state.value === "pending",
      v => {
        if (v) {
          store.commit("session/UNSET_RECORD_TRANSCRIPTION");
        }
      },
      { immediate: true }
    );

    const errCb = (
      consoleMessage: string,
      toastrMessage: string,
      err?: any
    ) => {
      console.error(`${consoleMessage}: `, err);
      showToastError(toastrMessage);
    };

    const audioContext = new AudioContext();

    const stopTranscription = async () =>
      await store.dispatch("session/stopTranscription");

    const expertTranslation = computed(
        (): { text: string; audio?: Uint8Array } =>
          store.state.session.recordTranslation
      ),
      isExpertTranslationPaused = ref(false),
      unsetExpertTranslation = () =>
        store.commit("session/UNSET_RECORD_TRANSLATION");

    const getDuration = (text: string) => text.length * 1000;

    let audio: AudioBufferSourceNode | null;

    const stopPlaying = () => {
      audio?.stop();
      audio = null;
    };

    const isSubtitlesEnabled = computed(() =>
      getProperty("aws.displaying.translated.subtitles.enabled")
    );

    const key = ref(0);

    watch(
      () => expertTranslation.value,
      v => {
        isExpertTranslationPaused.value = false;
        stopPlaying();
        key.value++;

        if (v) {
          if (
            v.audio
            // && getProperty("aws.playing.translated.audio.enabled")
          ) {
            const buffer = new Uint8Array(v.audio.length);
            buffer.set(new Uint8Array(v.audio), 0);
            audioContext
              .decodeAudioData(buffer.buffer)
              .then(audioBuffer => {
                audio = audioContext.createBufferSource();
                audio.loop = false;
                audio.onended = () => {
                  /* empty */
                };
                audio.buffer = audioBuffer;
                audio.connect(audioContext.destination);
                audio.start(0);
              })
              .catch(err =>
                errCb(
                  "Can not play translation audio",
                  i18n.global.t("call.error.recordTranslation.play"),
                  err
                )
              );
          }
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      stopTranscription();
      stopPlaying();
      unsetExpertTranslation();
      state.value = "pending";
      _.forEach(
        [
          RequestedAction.pauseTranslation,
          RequestedAction.startTranscription,
          RequestedAction.translateTranscription
        ],
        a => store.commit("session/UNSET_REQUESTED_ACTION", a)
      );
    });

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.pauseTranslation
        ),
      v => {
        if (v) {
          isExpertTranslationPaused.value = true;
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.pauseTranslation
          );
        }
      },
      { immediate: true }
    );

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.startTranscription
        ),
      async v => {
        if (v) {
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.startTranscription
          );
          state.value = "transcribing";

          try {
            await store.dispatch("session/startTranscription");
          } catch (err) {
            errCb(
              "Can not start transcription",
              i18n.global.t("call.error.recordTranslation.transcription"),
              err
            );
            state.value = "pending";
          }
        }
      },
      { immediate: true }
    );

    const translateTranscription = async () => {
      state.value = "translating";

      await stopTranscription();

      const isActual = () => state.value === "translating";

      if (!isActual()) {
        return;
      }

      if (!transcription.value) {
        errCb(
          "Can not translate the record",
          i18n.global.t("call.error.recordTranslation.translation")
        );
        state.value = "pending";
        return;
      }

      const data: {
        text: string;
        audio?: string;
      } = await store
        .dispatch("session/translate", transcription.value)
        .catch(err => {
          errCb(
            "Can not translate the record",
            i18n.global.t("call.error.recordTranslation.translation"),
            err
          );
          state.value = "pending";
          throw err;
        });

      if (!isActual()) {
        return;
      }

      store.dispatch("session/sendJsonMessage", {
        message: {
          type: MessageType.awsTranslationFinished,
          text: data.text,
          audioFilePath: data.audio ?? ""
        },
        callback: () => (state.value = "pending"),
        errorCallback: () => {
          errCb(
            "Can not send translation",
            i18n.global.t("call.error.recordTranslation.translation")
          );
          state.value = "pending";
        }
      });
    };

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.translateTranscription
        ),
      async v => {
        if (v) {
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.translateTranscription
          );
          await translateTranscription();
        }
      },
      { immediate: true }
    );

    return {
      state,
      isSubtitlesEnabled,
      transcription,
      expertTranslation,
      isExpertTranslationPaused,
      unsetExpertTranslation,
      getDuration,
      key,
      sourceLanguage,
      targetLanguage,
      getProperty,
      handleTranslateClick: () => translateTranscription(),
      HumanSpeechLanguage,
      RequestedAction,
      ..._.pick(_, ["split", "map", "trim"])
    };
  }
};
</script>
<style lang="scss">
@import "@/assets/styles/translate.scss";
</style>
